'use client'

import { MeiliConsolidatedBookDto } from '@hermes/domains'
import { Box, Rating, Stack, Typography, useTheme } from '@hermes/web-components'
import { useNavigation } from '@hooks/useNavigation'
import useTranslateMessage from '@hooks/useTranslateMessage'
import { useAnalytics } from '@lib/contexts/analyticsContext'
import Image from 'next/image'
import { usePathname } from 'next/navigation'
import { useMemo, useState } from 'react'
import { BookVersionsModal } from './BookVersionsModal'
import { SearchResultVariants } from './SearchResultVariants'

type SearchResultHorizontalProps = {
  book: MeiliConsolidatedBookDto
}

type MeiliBookVariant = MeiliConsolidatedBookDto['books'][number]

export const ImageResizable = ({ book }: { book: MeiliBookVariant }) => {
  const theme = useTheme()
  const { down } = theme.breakpoints
  const MAX_TOTAL_HEIGHT = 209
  const MAX_IMAGE_WIDTH = 130
  const MAX_HEIGHT_MOBILE = 105
  const MAX_WIDTH_MOBILE = 65
  const ImageUrl = useMemo(
    () =>
      (book.smallImage !== 'https://static-no.bookis.com/null' ? book.smallImage : null) ||
      (book.fullImage !== 'https://static-no.bookis.com/null' ? book.fullImage : null) ||
      '/img/default-cover.png',
    [book]
  )

  return (
    <Box
      sx={{
        height: MAX_TOTAL_HEIGHT,
        width: MAX_IMAGE_WIDTH,
        [down('sm')]: { height: MAX_HEIGHT_MOBILE, width: MAX_WIDTH_MOBILE },
        cursor: 'pointer'
      }}
      flexShrink={0}
      position={'relative'}
    >
      <Image
        src={ImageUrl}
        fill
        style={{
          objectFit: 'contain',
          overflow: 'hidden',
          borderRadius: '4px',
          backgroundColor:
            ImageUrl !== '/img/default-cover.png' ? `${book.imageDominateColor}` || theme.palette.divider : undefined
        }}
        alt={book?.title}
      />
    </Box>
  )
}

export const SearchResultHorizontal = ({ book }: SearchResultHorizontalProps) => {
  const { t } = useTranslateMessage()
  const navigation = useNavigation()
  const theme = useTheme()
  const analytics = useAnalytics()
  const pathname = usePathname()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const { down } = theme.breakpoints

  const defaultBook = book.books[0]
  const primaryAuthors = book.authors.primaryRoles

  const handleClick = () => {
    analytics.trackEvent('PRODUCT_CLICKED', {
      productId: defaultBook.id,
      page: pathname || '/handleSearchResultClick'
    })
    book.books.length > 1 ? setModalOpen(true) : navigation.navigate.productPage(defaultBook.slug)
  }

  return (
    <>
      <BookVersionsModal versions={book.books} isOpen={modalOpen} onClose={() => setModalOpen(false)} />
      <Box
        width={'100%'}
        justifyContent={'space-between'}
        padding={'12px'}
        gap="16px"
        display="flex"
        onClick={handleClick}
        zIndex={100}
      >
        <Box display="flex" gap="16px">
          <ImageResizable book={defaultBook} />
          <Stack justifyContent="space-evenly" spacing={3} style={{ cursor: 'pointer' }} display="flex">
            <Box
              sx={{
                [down('xs')]: { display: 'flex', flexDirection: 'column' }
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                  maxHeight: '3.6em',
                  lineHeight: '1.8em',
                  [down('sm')]: { lineHeight: '1.2em', maxHeight: '2.4em' }
                }}
              >
                {defaultBook.title}
                {defaultBook.subtitle && (
                  <>
                    {': '}
                    {defaultBook.subtitle}
                  </>
                )}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  wordWrap: 'break-word'
                }}
              >
                {primaryAuthors?.map(
                  (author, index) => `${author.fullName}${index < primaryAuthors.length - 1 ? ', ' : ''}`
                )}
              </Typography>
            </Box>
            {defaultBook.description && (
              <Typography
                variant="subtitle1"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                  whiteSpace: 'no-wrap',
                  wordBreak: 'break-word',
                  lineHeight: '1.5em',
                  maxHeight: '4.5em',
                  [down('sm')]: { display: 'none' }
                }}
              >
                {defaultBook.description}
              </Typography>
            )}

            <Box display="inline-flex" gap="8px" sx={{ [down('xs')]: { display: 'none' } }}>
              <Rating readOnly value={book.ratingAvg ?? 0} />
              <Typography fontSize="14px" lineHeight={1} paddingTop="1px">
                {t(
                  { id: 'review.ratingLabel' },
                  {
                    max: 5,
                    value: book.ratingAvg?.toFixed(1) ?? 0
                  }
                )}
              </Typography>
            </Box>
          </Stack>
        </Box>
        <SearchResultVariants bookHit={book} setModalOpen={setModalOpen} />
      </Box>
    </>
  )
}
